import AdminLayout from "@/components/layout-admin";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Banknote, Clock, CheckCircle2, DollarSign, Trash2 } from "lucide-react";

type GuestPayment = {
  id: number;
  organizationId: number;
  reservationId: number;
  amount: string;
  currency: string;
  method: string;
  status: string;
  dueDate: string | null;
  paidAt: string | null;
  externalRef: string | null;
  notes: string | null;
  createdAt: string | null;
};

type Reservation = {
  id: number;
  guestName: string;
  checkInDate: string;
};

const METHOD_LABELS: Record<string, string> = {
  cash: "Cash",
  credit_card: "Credit Card",
  bank_transfer: "Bank Transfer",
  paypal: "PayPal",
  other: "Other",
};

const STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  pending: "outline",
  paid: "default",
  refunded: "secondary",
  cancelled: "destructive",
};

const emptyForm = {
  reservationId: "",
  amount: "",
  currency: "USD",
  method: "cash",
  status: "pending",
  dueDate: "",
  notes: "",
};

export default function AdminGuestPayments() {
  const { toast } = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });

  const { data: payments, isLoading } = useQuery<GuestPayment[]>({
    queryKey: ["/api/admin/guest-payments"],
  });

  const { data: reservations } = useQuery<Reservation[]>({
    queryKey: ["/api/admin/reservations"],
  });

  const reservationMap = new Map<number, Reservation>();
  reservations?.forEach((r) => reservationMap.set(r.id, r));

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      await apiRequest("POST", "/api/admin/guest-payments", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/guest-payments"] });
      setCreateOpen(false);
      setForm({ ...emptyForm });
      toast({ title: "Payment recorded" });
    },
    onError: () => {
      toast({ title: "Failed to record payment", variant: "destructive" });
    },
  });

  const markPaidMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("PUT", `/api/admin/guest-payments/${id}`, {
        status: "paid",
        paidAt: new Date().toISOString(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/guest-payments"] });
      toast({ title: "Payment marked as paid" });
    },
    onError: () => {
      toast({ title: "Failed to update payment", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/admin/guest-payments/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/guest-payments"] });
      toast({ title: "Payment deleted" });
    },
    onError: () => {
      toast({ title: "Failed to delete payment", variant: "destructive" });
    },
  });

  const handleCreate = () => {
    if (!form.reservationId || !form.amount) return;
    createMutation.mutate({
      reservationId: Number(form.reservationId),
      amount: form.amount,
      currency: form.currency,
      method: form.method,
      status: form.status,
      dueDate: form.dueDate || null,
      notes: form.notes || null,
    });
  };

  const pendingPayments = payments?.filter((p) => p.status === "pending") ?? [];
  const paidPayments = payments?.filter((p) => p.status === "paid") ?? [];
  const totalAmount = payments?.reduce((sum, p) => sum + Number(p.amount), 0) ?? 0;
  const totalPending = pendingPayments.reduce((sum, p) => sum + Number(p.amount), 0);
  const totalPaid = paidPayments.reduce((sum, p) => sum + Number(p.amount), 0);

  const formatDate = (d: string | null) => {
    if (!d) return "-";
    return new Date(d).toLocaleDateString("en-US", { timeZone: "UTC" });
  };

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">
            Guest Payments
          </h1>
          <p className="text-muted-foreground mt-2">
            Track and manage payment records for guest reservations.
          </p>
        </div>

        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-add-payment">
              <Plus className="w-4 h-4 mr-2" /> Record Payment
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Record New Payment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Reservation</label>
                <Select
                  value={form.reservationId}
                  onValueChange={(v) => setForm({ ...form, reservationId: v })}
                >
                  <SelectTrigger data-testid="select-reservation">
                    <SelectValue placeholder="Select reservation" />
                  </SelectTrigger>
                  <SelectContent>
                    {reservations?.map((r) => (
                      <SelectItem key={r.id} value={String(r.id)}>
                        {r.guestName} - {new Date(r.checkInDate).toLocaleDateString("en-US", { timeZone: "UTC" })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Amount</label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  data-testid="input-amount"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Currency</label>
                <Input
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  data-testid="input-currency"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Method</label>
                <Select
                  value={form.method}
                  onValueChange={(v) => setForm({ ...form, method: v })}
                >
                  <SelectTrigger data-testid="select-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="paypal">PayPal</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Status</label>
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm({ ...form, status: v })}
                >
                  <SelectTrigger data-testid="select-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="refunded">Refunded</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Due Date</label>
                <Input
                  type="date"
                  value={form.dueDate}
                  onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                  data-testid="input-due-date"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Notes</label>
                <Textarea
                  placeholder="Optional notes..."
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="input-notes"
                />
              </div>
              <Button
                className="w-full"
                onClick={handleCreate}
                disabled={createMutation.isPending}
                data-testid="button-submit-payment"
              >
                {createMutation.isPending ? "Saving..." : "Record Payment"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 md:grid-cols-3 mb-8">
        <Card>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-yellow-500/10 rounded-xl flex-shrink-0">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Pending</p>
              <p className="text-2xl font-bold" data-testid="text-total-pending">
                ${totalPending.toFixed(2)}
              </p>
              <p className="text-xs text-muted-foreground">{pendingPayments.length} payments</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-green-500/10 rounded-xl flex-shrink-0">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Paid</p>
              <p className="text-2xl font-bold" data-testid="text-total-paid">
                ${totalPaid.toFixed(2)}
              </p>
              <p className="text-xs text-muted-foreground">{paidPayments.length} payments</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-xl flex-shrink-0">
              <DollarSign className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Amount</p>
              <p className="text-2xl font-bold" data-testid="text-total-amount">
                ${totalAmount.toFixed(2)}
              </p>
              <p className="text-xs text-muted-foreground">{payments?.length ?? 0} payments</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {isLoading && (
        <div className="text-center py-12 text-muted-foreground">Loading payments...</div>
      )}

      {!isLoading && (!payments || payments.length === 0) && (
        <div className="text-center py-12" data-testid="text-empty">
          <Banknote className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No payments recorded yet. Record your first payment to get started.</p>
        </div>
      )}

      {!isLoading && payments && payments.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Guest</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Paid Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((p) => {
                  const reservation = reservationMap.get(p.reservationId);
                  return (
                    <TableRow key={p.id} data-testid={`row-payment-${p.id}`}>
                      <TableCell data-testid={`text-guest-name-${p.id}`}>
                        {reservation?.guestName ?? `Reservation #${p.reservationId}`}
                      </TableCell>
                      <TableCell data-testid={`text-amount-${p.id}`}>
                        {p.currency} {Number(p.amount).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" data-testid={`badge-method-${p.id}`}>
                          {METHOD_LABELS[p.method] ?? p.method}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={STATUS_VARIANTS[p.status] ?? "outline"}
                          data-testid={`badge-status-${p.id}`}
                        >
                          {p.status}
                        </Badge>
                      </TableCell>
                      <TableCell data-testid={`text-due-date-${p.id}`}>
                        {formatDate(p.dueDate)}
                      </TableCell>
                      <TableCell data-testid={`text-paid-date-${p.id}`}>
                        {formatDate(p.paidAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-1 justify-end flex-wrap">
                          {p.status === "pending" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => markPaidMutation.mutate(p.id)}
                              disabled={markPaidMutation.isPending}
                              data-testid={`button-mark-paid-${p.id}`}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" /> Mark Paid
                            </Button>
                          )}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="icon"
                                variant="ghost"
                                data-testid={`button-delete-payment-${p.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Payment</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete this payment record? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel data-testid="button-cancel-delete">
                                  Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deleteMutation.mutate(p.id)}
                                  data-testid="button-confirm-delete"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
}
